import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClassificationForm } from 'components/molecules';
import { Button, Logo } from 'components/atoms';
import { MainContentProps } from 'types/components.types';
import { LOGO_USAGE, LOGO_ALT_TEXT } from 'constants/logo.constants';
import useSidebarStore from 'stores/sidebarStore';

const MainContent: React.FC<MainContentProps> = ({ 
  onStartChat,
  onLogin,
  onSignUp,
  className = '',
  isAuthenticated = false
}) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebarStore();

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      navigate('/login');
    }
  };

  const handleSignUp = () => {
    if (onSignUp) {
      onSignUp();
    } else {
      navigate('/signup');
    }
  };

  const handleStartChat = (file?: File | null, message?: string, options?: { skipTariff?: boolean }) => {
    if (onStartChat) {
      onStartChat(file, message, options);
    }
  };

  return (
    <div className={`flex-1 ${isCollapsed ? 'ml-[72px]' : 'ml-64'} flex items-center justify-center p-10 min-h-screen transition-all duration-200 ${className}`}>
      <div className="max-w-2xl w-full text-center">
        <div className="mb-10">
          <div className="flex items-center justify-center mb-6">
            <Logo 
              variant={LOGO_USAGE.HOMEPAGE_HERO.variant as 'full' | 'long' | 'icon'}
              size={LOGO_USAGE.HOMEPAGE_HERO.size as 'sm' | 'md' | 'lg' | 'xl'}
              alt={LOGO_ALT_TEXT.FULL}
            />
          </div>
          <h1 className="text-[34px] font-semibold text-gray-900 leading-tight mb-3 max-w-lg mx-auto">
            What will you Classfi today?
          </h1>
          <p className="text-[14px] text-gray-600">
            Enter your product's description, country of origin, and its composition or ingredients
          </p>
        </div>
        
        <div className="mb-8">
          <ClassificationForm onStartChat={handleStartChat} />
        </div>

        {/* Example Cards Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-[12px] font-medium text-gray-600">Here's a Helpful Example</h3>
            <button type="button" className="text-[12px] font-medium text-gray-600 hover:text-gray-800">More Examples?</button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {/* Example Card 1 */}
            <div
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleStartChat(null, "What is the HTS code for a desktop Computer's LED monitor?")}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-[12px] text-gray-700 text-left">
                  What is the HTS code for a desktop Computer's LED monitor?
                </p>
              </div>
            </div>

            {/* Example Card 2 */}
            <div
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleStartChat(null, "HTS code and duty rate for lithium-ion batteries?")}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-[12px] text-gray-700 text-left">
                  HTS code and duty rate for lithium-ion batteries?
                </p>
              </div>
            </div>

            {/* Example Card 3 */}
            <div
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleStartChat(null, "Classify these plastic storage containers. Provide HTS + duty info")}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <p className="text-[12px] text-gray-700 text-left">
                  Classify these plastic storage containers. Provide HTS + duty info
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Auth buttons - only show when NOT authenticated */}
        {!isAuthenticated && (
          <div className="flex gap-4 justify-center mb-8">
            <Button
              onClick={handleLogin}
              variant="outline"
              className="text-[12px]"
            >
              Sign In
            </Button>
            <Button
              onClick={handleSignUp}
              variant="teal"
              className="text-[12px]"
            >
              Get Started
            </Button>
          </div>
        )}
        
        <div className="text-center mt-8">
          <p className="text-[10px] text-gray-400 leading-relaxed max-w-2xl mx-auto">
            By messaging Classfi, you agree to our{' '}
            <a href="https://www.classfi.ai/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Terms</a>{' '}
            and have read our{' '}
            <a href="https://www.classfi.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MainContent;
